import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Box, useColorModeValue } from "@chakra-ui/react";
import { useCopyValue } from "hooks/useCopies";
import type { DealInputSuggestion, DealFilterAction } from "types/copy";
import { useConversationDialogSubmitFunctions } from "screens/thread/useConversationDialogSubmitFunctions";
import { useConversationContext } from "screens/thread/ConversationContext";
import { useLocation } from "react-router-dom";

interface DealActionButtonsProps {
  size?: "sm" | "md";
}

export const DealActionButtons: React.FC<DealActionButtonsProps> = ({ size = "sm" }) => {
  const [activeButtonIndex, setActiveButtonIndex] = useState<number | null>(null);
  const [lastProcessedSearchQuery, setLastProcessedSearchQuery] = useState<string | null>(null);
  const bgColor = useColorModeValue("transparent", "gray.700");
  const activeBgColor = useColorModeValue("primary.default", "primary.400");
  const fontColor = useColorModeValue("white", "gray.600");
  const { onSubmitDealFinderQuery } = useConversationDialogSubmitFunctions({});
  const { setActiveFilter, setActiveFilterUrlKey, activeFilterUrlKey, isAnotherInputFocused, shouldClearInputValue } =
    useConversationContext();
  const location = useLocation();

  const rawDealSuggestions = useCopyValue("copy_deal_input_suggestions", true) as (string | DealInputSuggestion)[] | undefined;
  const rawDealFilterActions = useCopyValue("copy_deal_filter_actions", true) as (string | DealFilterAction)[] | undefined;

  const dealActionButtons = useMemo(() => {
    const suggestions = rawDealSuggestions
      ? rawDealSuggestions
          .map((suggestion) => {
            if (typeof suggestion === "string") {
              try {
                return JSON.parse(suggestion) as DealInputSuggestion;
              } catch {
                return { key: suggestion, question: suggestion, label: suggestion, type: "quick_link" as const };
              }
            }
            return suggestion;
          })
          .filter((suggestion) => suggestion.type === "quick_link")
          .map((suggestion) => ({ ...suggestion, query: suggestion.question }))
      : [];

    const filterActions = rawDealFilterActions
      ? rawDealFilterActions
          .map((action) => {
            if (typeof action === "string") {
              try {
                return JSON.parse(action) as DealFilterAction;
              } catch {
                return null;
              }
            }
            return action;
          })
          .filter((action): action is DealFilterAction => action !== null)
          .map((action) => ({ ...action, query: action.url }))
      : [];

    return [...filterActions, ...suggestions];
  }, [rawDealSuggestions, rawDealFilterActions]);

  const isMdSize = size === "md";
  const filterButtonPx = isMdSize ? "10px" : "5px";
  const filterButtonPy = isMdSize ? "3px" : "1px";
  const filterButtonFontSize = isMdSize ? "13px" : "10px";
  const filterButtonBorderRadius = "full";

  const handleButtonClick = useCallback(
    (item: (DealInputSuggestion | DealFilterAction) & { query: string }, index: number) => {
      if (activeButtonIndex === index) {
        return;
      }
      setActiveButtonIndex(index);

      if ("url" in item) {
        setActiveFilterUrlKey(item.key);
        setActiveFilter(null);
      } else {
        onSubmitDealFinderQuery(item.query);
        setActiveFilterUrlKey(null);
      }
    },
    [activeButtonIndex, onSubmitDealFinderQuery, setActiveFilterUrlKey, setActiveFilter]
  );

  useEffect(() => {
    if (isAnotherInputFocused || shouldClearInputValue) {
      setActiveButtonIndex(null);
    }
  }, [isAnotherInputFocused, shouldClearInputValue]);

  // Handle URL query parameter for automatic filter activation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get("search");
    const dealId = searchParams.get("dealId");

    // Only process if search query has changed and dealId is not present
    if (searchQuery && dealActionButtons.length > 0 && !dealId && searchQuery !== lastProcessedSearchQuery) {
      // Find the item that matches the search query by key
      const matchingItemIndex = dealActionButtons.findIndex((item) => item.key === searchQuery);

      if (matchingItemIndex !== -1) {
        const matchingItem = dealActionButtons[matchingItemIndex];

        // Only activate if it's a URL-based item (DealFilterAction)
        if ("url" in matchingItem) {
          setActiveFilterUrlKey(matchingItem.key);
          setActiveFilter(null);
          setActiveButtonIndex(matchingItemIndex);
          setLastProcessedSearchQuery(searchQuery);
        }
      }
    }

    // Clear the last processed query if search query is removed
    if (!searchQuery && lastProcessedSearchQuery) {
      setLastProcessedSearchQuery(null);
    }
  }, [location.search, dealActionButtons, setActiveFilterUrlKey, setActiveFilter, lastProcessedSearchQuery]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveButtonIndex(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  if (dealActionButtons.length === 0) {
    return null;
  }

  return (
    <>
      {dealActionButtons.map((suggestion, index) => {
        const isActive = activeButtonIndex === index || ("url" in suggestion && activeFilterUrlKey === suggestion.url);
        const isDisabled = isActive;

        return (
          <Box
            key={"key" in suggestion ? suggestion.key : `deal-action-${index}`}
            className={`ch-filter-ticker-button ch-query-deal ch-filter-ticker-${isActive ? "active" : "inactive"}`}
            as="button"
            px={filterButtonPx}
            py={filterButtonPy}
            borderRadius={filterButtonBorderRadius}
            fontSize={filterButtonFontSize}
            whiteSpace="nowrap"
            fontWeight="semibold"
            bg={isActive ? activeBgColor : bgColor}
            color={fontColor}
            disabled={isDisabled}
            _hover={{
              bg: activeBgColor,
            }}
            _active={{}}
            border="2px solid"
            borderColor={fontColor}
            cursor={isDisabled ? "not-allowed" : "pointer"}
            onClick={(e) => {
              e.stopPropagation();
              handleButtonClick(suggestion, index);
            }}
            transition="all 0.2s"
            style={{ scrollSnapAlign: "start" }}
            display="flex"
            alignItems="center"
            gap="6px">
            {suggestion.label}
          </Box>
        );
      })}
    </>
  );
};
